/**
 * Loads one Pacific day of Patient Access activity and derives the manager's report.
 *
 * Aggregated in the browser rather than by a rollup bot: none of these figures are
 * FHIR-searchable (pickup, duration and routing all live in base64 JSON inside
 * Communication extensions), real inbound volume is under 100 calls a day, and
 * deriving on read keeps the current hour honest — a call still in progress shows
 * as live instead of waiting for a batch. Revisit if volume grows by an order of
 * magnitude; it is the extension payloads that get heavy, not the row count.
 */

import { PAC_INTRO_CALL_CODE, PAC_IVR_BRANCHES } from '@bariendo/lib/patient-access';
import { buildCiDailyReport, PacCoordinator, PacDailyReport } from '@bariendo/lib/pac-daily-report';
import {
  APPOINTMENT_TYPES_SYSTEM,
  BARIENDO_CT_SYSTEM,
  CommunicationCategory,
  FlagCategory,
  IvrHoursSetId,
  ProviderAvailabilityStatus,
  ProviderRole,
  ScheduleCategory,
} from '@bariendo/lib/constants';
import { fetchIvrHoursSets, IvrHoursSet } from '@bariendo/lib/ivr';
import { getPrimaryProviderRole } from '@bariendo/lib/profile';
import { formatHumanName } from '@medplum/core';
import {
  Appointment,
  Communication,
  Flag,
  Patient,
  Practitioner,
  PractitionerRole,
  Schedule,
} from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useEffect, useState } from 'react';

dayjs.extend(utc);
dayjs.extend(timezone);

/** The report is a Pacific-day view; every boundary below is PT, never browser-local. */
export const PAC_REPORT_TIMEZONE = 'America/Los_Angeles';

/**
 * Fallback business hours when no IVR intake hours set is configured.
 *
 * Without this the whole day would read as after hours, which looks like a
 * staffing catastrophe rather than a missing config.
 */
const FALLBACK_BUSINESS_HOURS: IvrHoursSet = {
  hoursSetId: IvrHoursSetId.INTAKE,
  name: 'Intake',
  timezone: PAC_REPORT_TIMEZONE,
  weekday: { startTime: '09:00:00', endTime: '17:00:00' },
};

export interface UseCiDailyReportResult {
  report: PacDailyReport | undefined;
  /**
   * Live availability per coordinator, present only when the day being viewed
   * is today. The `Flag` behind it carries no period, so it describes right now
   * and nothing else — attaching it to a past day would be a fabrication.
   */
  availability: Record<string, ProviderAvailabilityStatus> | undefined;
  /** `Patient/<id>` → display name, for call-log callers whose number matched a patient. */
  patientNames: Record<string, string>;
  /** `Practitioner/<id>` → display name, for whoever answered a call. */
  practitionerNames: Record<string, string>;
  loading: boolean;
  error: string | undefined;
}

/**
 * Display names for the references the call log renders.
 *
 * One search per resource type rather than one per row: a busy day is a few
 * hundred calls over far fewer distinct people, and `_id` takes a comma-joined
 * list. References that resolve to nothing are simply absent, and the log falls
 * back to the phone number or the raw reference.
 */
async function fetchDisplayNames(
  medplum: ReturnType<typeof useMedplum>,
  resourceType: 'Patient' | 'Practitioner',
  // Takes the optional references straight off the rows, so a caller cannot
  // forget to filter and silently drop a whole class of row.
  references: (string | undefined)[]
): Promise<Record<string, string>> {
  const ids = [...new Set(references)]
    .map((reference) => reference?.split('/')[1])
    .filter((id): id is string => Boolean(id));

  if (ids.length === 0) {
    return {};
  }

  const resources = await medplum.searchResources(resourceType, {
    _id: ids.join(','),
    _count: String(Math.min(ids.length, 1000)),
  });

  const names: Record<string, string> = {};
  for (const resource of resources as (Patient | Practitioner)[]) {
    const name = resource.name?.[0];
    if (resource.id && name) {
      names[`${resourceType}/${resource.id}`] = formatHumanName(name);
    }
  }

  return names;
}

function pacificDayRange(date: string): { start: string; end: string } {
  const startOfDay = dayjs.tz(date, PAC_REPORT_TIMEZONE).startOf('day');
  return {
    start: startOfDay.toISOString(),
    end: startOfDay.add(1, 'day').toISOString(),
  };
}

interface PacRoster {
  coordinators: PacCoordinator[];
  /** `PractitionerRole/<id>` → `Practitioner/<id>`, to attribute Schedules whose actor is the role. */
  practitionerByRole: Record<string, string>;
}

async function fetchRoster(medplum: ReturnType<typeof useMedplum>): Promise<PacRoster> {
  const roles = await medplum.searchResources('PractitionerRole', {
    // 768821004 — PAC, care team coordinator.
    role: ProviderRole.PATIENT_ACCESS,
    _include: 'PractitionerRole:practitioner',
    _count: '100',
  });

  const coordinators: PacCoordinator[] = [];
  const practitionerByRole: Record<string, string> = {};
  const seen = new Set<string>();

  for (const role of roles as PractitionerRole[]) {
    if (getPrimaryProviderRole(role) !== ProviderRole.PATIENT_ACCESS) {
      continue;
    }

    const reference = role.practitioner?.reference;
    if (!reference) {
      continue;
    }

    // A second PractitionerRole for the same person still contributes its
    // Schedules, but only one row.
    if (role.id) {
      practitionerByRole[`PractitionerRole/${role.id}`] = reference;
    }

    if (seen.has(reference)) {
      continue;
    }
    seen.add(reference);

    const practitioner = role.practitioner?.resource as Practitioner | undefined;
    coordinators.push({
      reference,
      name: practitioner?.name?.[0] ? formatHumanName(practitioner.name[0]) : (role.practitioner?.display ?? reference),
    });
  }

  coordinators.sort((left, right) => left.name.localeCompare(right.name));
  return { coordinators, practitionerByRole };
}

/**
 * The roster's appointment-availability Schedules, keyed by coordinator.
 *
 * These are the occupancy denominator. Today nobody outside test data has one —
 * there is no Patient Access team yet — so the column reads as unknown rather than
 * zero, and starts working the day real coordinators are provisioned.
 */
async function fetchCoordinatorSchedules(
  medplum: ReturnType<typeof useMedplum>,
  practitionerByRole: Record<string, string>
): Promise<Record<string, Schedule[]>> {
  const roleReferences = Object.keys(practitionerByRole);
  if (roleReferences.length === 0) {
    return {};
  }

  const schedules = await medplum.searchResources('Schedule', {
    actor: roleReferences.join(','),
    'service-category': `${BARIENDO_CT_SYSTEM}|${ScheduleCategory.APPOINTMENT_AVAILABILITY}`,
    active: true,
    _count: '200',
  });

  const byCoordinator: Record<string, Schedule[]> = {};
  for (const schedule of schedules as Schedule[]) {
    for (const actor of schedule.actor ?? []) {
      const coordinator = actor.reference ? practitionerByRole[actor.reference] : undefined;
      if (coordinator) {
        byCoordinator[coordinator] = [...(byCoordinator[coordinator] ?? []), schedule];
      }
    }
  }

  return byCoordinator;
}

/**
 * Live availability, keyed by coordinator.
 *
 * One `Flag` per practitioner with two states and no `period`, so this is a
 * point-in-time read and says nothing about the past — which is why the table
 * only shows it when the day being viewed is today. A coordinator with no Flag
 * is treated as online, matching what call routing believes about them.
 */
async function fetchAvailabilityStatuses(
  medplum: ReturnType<typeof useMedplum>,
  coordinators: PacCoordinator[]
): Promise<Record<string, ProviderAvailabilityStatus>> {
  if (coordinators.length === 0) {
    return {};
  }

  const flags = await medplum.searchResources('Flag', {
    subject: coordinators.map((coordinator) => coordinator.reference).join(','),
    category: FlagCategory.PROVIDER_REAL_TIME_AVAILABILITY,
    status: 'active',
    _count: '200',
  });

  const byCoordinator: Record<string, ProviderAvailabilityStatus> = {};
  for (const flag of flags as Flag[]) {
    const subject = flag.subject?.reference;
    const code = flag.code?.coding?.[0]?.code;
    if (subject && code === ProviderAvailabilityStatus.DO_NOT_DISTURB) {
      byCoordinator[subject] = ProviderAvailabilityStatus.DO_NOT_DISTURB;
    } else if (subject && code === ProviderAvailabilityStatus.ONLINE) {
      byCoordinator[subject] = ProviderAvailabilityStatus.ONLINE;
    }
  }

  return byCoordinator;
}

export function usePacDailyReport(date: string): UseCiDailyReportResult {
  const medplum = useMedplum();
  const [report, setReport] = useState<PacDailyReport | undefined>();
  const [availability, setAvailability] = useState<Record<string, ProviderAvailabilityStatus> | undefined>();
  const [patientNames, setPatientNames] = useState<Record<string, string>>({});
  const [practitionerNames, setPractitionerNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      setLoading(true);
      setError(undefined);

      const { start, end } = pacificDayRange(date);
      const isToday = date === todayInPacific();

      try {
        const [calls, introCallAppointments, introCallNotes, roster, hoursSets] = await Promise.all([
          // `sent` rather than `_lastUpdated`: a call belongs to the hour it came
          // in, not the hour its last webhook happened to land.
          medplum.searchResources('Communication', [
            ['category', CommunicationCategory.PHONE_CALL],
            ['sent', `ge${start}`],
            ['sent', `lt${end}`],
            ['_count', '1000'],
          ]),
          medplum.searchResources('Appointment', [
            ['appointment-type', `${APPOINTMENT_TYPES_SYSTEM}|${PAC_INTRO_CALL_CODE}`],
            ['date', `ge${start}`],
            ['date', `lt${end}`],
            ['_count', '1000'],
          ]),
          medplum.searchResources('Communication', [
            ['category', CommunicationCategory.PAC_INTRO_CALL_NOTE],
            ['sent', `ge${start}`],
            ['sent', `lt${end}`],
            ['_count', '1000'],
          ]),
          fetchRoster(medplum),
          fetchIvrHoursSets(medplum),
        ]);

        if (cancelled) {
          return;
        }

        // Both depend on the roster, so they cannot join the batch above.
        const [schedulesByCoordinator, availabilityByCoordinator] = await Promise.all([
          fetchCoordinatorSchedules(medplum, roster.practitionerByRole),
          isToday ? fetchAvailabilityStatuses(medplum, roster.coordinators) : Promise.resolve(undefined),
        ]);

        if (cancelled) {
          return;
        }

        const nextReport = buildCiDailyReport({
          calls: calls as Communication[],
          introCallAppointments: introCallAppointments as Appointment[],
          introCallNotes: introCallNotes as Communication[],
          coordinators: roster.coordinators,
          hoursSet: hoursSets.get(IvrHoursSetId.INTAKE) ?? FALLBACK_BUSINESS_HOURS,
          timezone: PAC_REPORT_TIMEZONE,
          date,
          schedulesByCoordinator,
          intakeIvrBranches: PAC_IVR_BRANCHES,
        });

        setAvailability(availabilityByCoordinator);
        setReport(nextReport);

        // Resolved from the derived rows rather than the raw bundle, so exactly
        // the references the log will render get looked up. Roster names are
        // already in hand; only answerers outside the roster need fetching.
        const logEntries = nextReport.hours.flatMap((bucket) => bucket.calls);
        // Scheduled intro calls are rows too, and their patient is nowhere in the
        // call entries. Leaving them out rendered every booked consult as
        // "Unknown patient" — which reads as "the caller was not recognised"
        // rather than "we never looked them up".
        const introCallEntries = nextReport.hours.flatMap((bucket) => bucket.introCalls);
        const rosterNames = Object.fromEntries(
          roster.coordinators.map((coordinator) => [coordinator.reference, coordinator.name])
        );
        const unknownPractitioners = [
          ...logEntries.map((entry) => entry.answeredBy),
          // An intro call can be held by someone off the PAC roster.
          ...introCallEntries.map((entry) => entry.practitionerReference),
        ].filter((reference): reference is string => Boolean(reference) && !rosterNames[reference!]);

        const [resolvedPatients, resolvedPractitioners] = await Promise.all([
          fetchDisplayNames(medplum, 'Patient', [
            ...logEntries.map((entry) => entry.patientReference),
            ...introCallEntries.map((entry) => entry.patientReference),
          ]),
          fetchDisplayNames(medplum, 'Practitioner', unknownPractitioners),
        ]);

        if (cancelled) {
          return;
        }

        setPatientNames(resolvedPatients);
        setPractitionerNames({ ...rosterNames, ...resolvedPractitioners });
      } catch (loadError) {
        if (cancelled) {
          return;
        }
        setReport(undefined);
        setAvailability(undefined);
        setPatientNames({});
        setPractitionerNames({});
        setError(loadError instanceof Error ? loadError.message : 'Could not load the daily report.');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load().catch(() => {
      // load() already routes failures into state; this guards an unhandled rejection.
    });

    return () => {
      cancelled = true;
    };
  }, [medplum, date]);

  return { report, availability, patientNames, practitionerNames, loading, error };
}

/** Exported for the page header, so the label and the query cannot disagree. */
export function formatPacificDay(date: string): string {
  return dayjs.tz(date, PAC_REPORT_TIMEZONE).format('ddd, MMM D, YYYY');
}

export function todayInPacific(): string {
  return dayjs().tz(PAC_REPORT_TIMEZONE).format('YYYY-MM-DD');
}

export function shiftPacificDay(date: string, days: number): string {
  return dayjs.tz(date, PAC_REPORT_TIMEZONE).add(days, 'day').format('YYYY-MM-DD');
}

export type { PacDailyReport };
